import { FC, useState } from 'react';
import { Badge, Card, Icon, Icons, MaterialSymbol, Tooltip, VerificationStatus, withStyles } from 'vopay';
import styles from './PaymentMethodListItem.module.scss';
import { PaymentMethodTypeIcon } from 'UI';
import { PaymentMethod } from '@/hooks/usePaymentMethods';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import AvailableVia from './AvailableVia/AvailableVia';

const cn = withStyles(styles);

const verificationIcons: Record<VerificationStatus, Icons> = {
  verified: 'checkGreenCircle',
  unverified: 'infoCircle',
  failed: 'errorRed',
};

type PaymentMethodListItemProps = {
  method: PaymentMethod;
  selected: boolean;
  active: boolean;
  onSetDefault: (method: PaymentMethod) => void;
};

const PaymentMethodListItem: FC<PaymentMethodListItemProps> = ({ method, selected, active, onSetDefault }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.ListItem' });
  const [addPaymentDropdownExpanded, setAddPaymentDropdownExpanded] = useState(false);

  const getVerificationMessage = (status: VerificationStatus, type: PaymentMethod['type']): string => {
    if (type === 'BankAccount') {
      const bankAccountMessages: Record<VerificationStatus, string> = {
        verified: t('BankAccount.verified', {
          defaultValue: 'Account Holder has been verified as the owner of this bank account.',
        }),
        unverified: t('BankAccount.unverified', { defaultValue: 'Bank account has not been verified.' }),
        failed: t('BankAccount.failed', { defaultValue: 'Bank account verification has failed.' }),
      };
      return bankAccountMessages[status];
    }

    // For Email, PhoneNumber, CreditCard, DebitCard
    const messages: Record<VerificationStatus, string> = {
      verified: t('verified', { defaultValue: 'Account verified' }),
      unverified: t('unverified', { defaultValue: 'Account has not been verified' }),
      failed: t('failed', { defaultValue: 'Account verification has failed' }),
    };

    return messages[status];
  };

  const showVerificationTooltip =
    method.verificationStatus && ['Email', 'BankAccount', 'CreditCard', 'DebitCard'].includes(method.type);

  const paymentMethods = method.kind !== 'HOLDER' ? [...(method.groupedItems ?? []), method] : method.groupedItems;
  const showManageDefaultDropdown =
    (method.type === 'Email' || method.type === 'PhoneNumber' || method.type == 'Venmo' || method.type == 'PayPal') &&
    paymentMethods?.some((item) => !item.isDefault) &&
    paymentMethods?.length > 1;

  return (
    <Card
      key={method.token}
      className={cn('method')}
      style={{
        padding: '16px 24px',
        borderWidth: selected ? 2 : undefined,
        borderColor: selected ? 'var(--vopayBlack)' : undefined,
        shadowOpacity: selected ? 0 : 0.1,
        backgroundColor: selected ? '#f1f1f1' : undefined,
        dropShadow: '0px 10px 12px #2d2d2d1a',
      }}
    >
      <PaymentMethodTypeIcon
        method={method.type}
        kind={method.kind ?? method.type}
        bankId={method.bankId}
        creditCardBrand={method.creditCardBrand}
      />

      <div className={cn('methodData')}>
        <div className={cn('accountNo')}>
          <span data-testid={'paymentMethod-details'}>{method.details}</span>

          {method.currency && (
            <>
              <span>|</span>

              <span>{method.currency}</span>
            </>
          )}

          {showVerificationTooltip && (
            <span>
              <Tooltip
                customElement={
                  <Icon
                    icon={verificationIcons[method.verificationStatus as VerificationStatus]}
                    size="md"
                    bgColor="none"
                    color="none"
                    width="24px"
                    height="24px"
                  />
                }
                hasButton={false}
                information={getVerificationMessage(method.verificationStatus as VerificationStatus, method.type)}
                icon="info"
                position="right"
              />
            </span>
          )}
        </div>
        <span className={cn('accountType')}>
          {method.typeLabel}

          {method.type === 'CreditCard' ? (
            method.expirationDate ? (
              <span>
                | {t('exp', { defaultValue: 'Exp' })}: {method.expirationDate}
              </span>
            ) : (
              <>
                <span>|</span>
                <span>{t('exp', { defaultValue: 'Exp' })}: -/-</span>
                <Tooltip
                  hasButton={false}
                  information={t('expirationDateNotSet', { defaultValue: 'Expiration date has not been set.' })}
                  icon="info"
                  position="right"
                  customElement={<MaterialSymbol icon="info" size="18px" />}
                />
              </>
            )
          ) : (
            <>
              <span>|</span>
              <span>{t('exp', { defaultValue: 'Exp' })}: -/-</span>
            </>
          )}
        </span>
        <div>
          <div className={cn('defaultArea')}>
            <AvailableVia method={method} />
            {showManageDefaultDropdown ? (
              <Button
                size="smallest"
                iconRight={addPaymentDropdownExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                onClick={(e) => {
                  e.stopPropagation();
                  setAddPaymentDropdownExpanded((prev) => !prev);
                }}
                disabled={!active}
                type="button"
                className={cn('defaultButtons')}
              >
                Manage Default
              </Button>
            ) : (
              <>
                {method.isDefault ? (
                  <Badge label={t('default', { defaultValue: 'Default' })} color="info" />
                ) : (
                  <Button
                    size="smallest"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetDefault(method);
                    }}
                    disabled={!active}
                    type="button"
                    className={cn('defaultButtons')}
                  >
                    {t('setAsDefault', { defaultValue: 'Set as Default' })}
                  </Button>
                )}
              </>
            )}
          </div>
          {addPaymentDropdownExpanded && (
            <div className={cn('manageDefaultDropdown')}>
              {paymentMethods?.map((item) => (
                <div key={item.token} className={cn('manageDefaultItem')}>
                  <div className={cn('label')}>{item.typeLabel}</div>
                  {item.isDefault ? (
                    <Badge label={t('default', { defaultValue: 'Default' })} color="info" />
                  ) : (
                    <Button
                      size="smallest"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDefault(item);
                      }}
                      type="button"
                      disabled={!active}
                      className={cn('defaultButtons')}
                    >
                      {t('setAsDefault', { defaultValue: 'Set as Default' })}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default PaymentMethodListItem;
